import SiteEnum from '@models/enums/SiteEnum';
import { IAddToWishlistProps } from '@models/IAddToWishlist';
import IFormValues from '@models/IFormValues';
import AddToWishlistLayout from '@molecules/AddToWishlistLayout/AddToWishlistLayout';
import { generateRandomNumber } from '@utility/helpers/generateRandomNumber';
import { useMyList } from '@utility/hooks/useMyList';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

const AddToMyListButton = (props: IAddToWishlistProps) => {
  const { wishlistDrawerLabels, onDrawerStateChange, onLikeOverride } = props;
  const { isLiked, addToMyList, removeFromMyList, myList, refreshMyList } =
    useMyList(props.cardId);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    onDrawerStateChange?.(isOpen);
  }, [isOpen]);

  const onSubmit: SubmitHandler<IFormValues> = (formData) => {
    const selectedLists = Object.entries(formData)
      .map((entry) => ({
        title: entry[0],
        id: typeof entry[1] === 'string' ? parseInt(entry[1]) : -1,
      }))
      .filter((entry) => entry.id > -1);

    const { cardId, collection, gtmTrackingEvents } = props;

    window.dataLayer.push({
      event: 'trackingEvent_add_to_my_list',
      event_name_variable: 'add_to_my_list',
      trackingEvent_add_to_my_list: {
        item_name: gtmTrackingEvents?.heading,
        item_id: gtmTrackingEvents?.standardNameId,
        item_cms_id: cardId,
        ...(gtmTrackingEvents?.displayCategory && {
          item_category: gtmTrackingEvents?.displayCategory,
        }),
        item_category2: collection,
      },
    });

    addToMyList(selectedLists);
    setIsOpen(false);
  };

  const handleLike = () => {
    if (onLikeOverride) {
      onLikeOverride();
      return;
    }

    if (isLiked) {
      removeFromMyList();
    } else {
      refreshMyList();
      setIsOpen(true);
    }
  };

  const newWishlistNames = useMemo(() => {
    const newNamePlaceholder = wishlistDrawerLabels?.newList.options?.[0];
    let newNames = [];
    let i = 1;
    const existingNames = myList?.map((list) => list.title);
    while (newNames.length < 5) {
      const numberedName = `${newNamePlaceholder} ${i}`;
      if (!existingNames?.includes(numberedName)) {
        newNames.push(numberedName);
      }
      i++;
    }
    return newNames.map((name) => ({
      label: name,
      id: generateRandomNumber(6),
    }));
  }, [myList, wishlistDrawerLabels?.newList.options]);

  const additionalProps = {
    handleLike,
    isLiked,
    isOpen,
    setIsOpen,
    existingWishlistNames:
      myList?.map((listItem) => ({
        label: listItem.title,
        id: listItem.id,
      })) ?? [],
    newWishlistNames,
    onSubmit,
  };

  return (
    <AddToWishlistLayout {...props} {...additionalProps} site={SiteEnum.B2B} />
  );
};

export default AddToMyListButton;
